import { useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '@/store/store';
import type { OrgProgramContextOverride } from '@/types/context';
import { currentUser } from '@/utils/user';

import { API_BASE_URL, joinApiUrl } from '../services/api.config';
import { refreshAccessToken } from '../services/auth.session';

type Phase = 'idle' | 'connecting' | 'streaming' | 'ready' | 'error';

interface StreamingProgress {
  received: number;
  total: number | null;
}

interface StreamingAudioResult {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  phase: Phase;
  progress: StreamingProgress;
  duration: number;
  /** End of the buffered range in seconds (for seekbar visualization). */
  bufferedEnd: number;
  error: string | null;
  retry: () => void;
}

/** Build auth + org headers for fetch (mirrors buildSSEHeaders in api.config.ts) */
function buildStreamHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const user = currentUser();
  if (user?.access_token) {
    headers['Authorization'] = `Bearer ${user.access_token}`;
  }
  const orgId = useStore.getState().selectedOrganizationId;
  if (orgId) {
    headers['x-org-id'] = orgId;
  }
  return headers;
}

/** Build the download URL for an asset */
function buildDownloadUrl(
  organizationId: string,
  programId: string,
  caseRecordId: string,
  assetId: string,
): string {
  return joinApiUrl(
    API_BASE_URL,
    `/organizations/${organizationId}/programs/${programId}/caserecords/${caseRecordId}/assets/${assetId}/download-from-arcus`,
  );
}

/**
 * Detect the correct MIME type for MSE SourceBuffer from a Content-Type header.
 * Falls back to audio/mpeg which is widely supported.
 */
function getMSEMimeType(contentType: string | null): string {
  if (!contentType) return 'audio/mpeg';
  // Strip charset/params
  const mime = contentType.split(';')[0].trim().toLowerCase();
  // Map common audio/video types to MSE-compatible types
  if (mime === 'audio/mp3' || mime === 'audio/mpeg') return 'audio/mpeg';
  if (mime === 'audio/aac') return 'audio/aac';
  if (mime === 'audio/mp4' || mime === 'video/mp4') return 'audio/mp4';
  // Default
  return 'audio/mpeg';
}

/**
 * Hook that streams audio via MediaSource Extensions.
 * Playback starts as chunks arrive — no full file download required.
 */

export function useStreamingAudio(
  caseRecordId: string | undefined,
  assetId: string | undefined,
  _callSid?: string,
  contextOverride?: OrgProgramContextOverride,
): StreamingAudioResult {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const orgId = contextOverride?.organizationId || selectedOrganizationId;
  const progId = contextOverride?.programId || selectedProgramId;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<StreamingProgress>({ received: 0, total: null });
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Stable identity for the "asset to load" — avoids re-triggering on unrelated renders
  const assetKey =
    assetId && orgId && progId && caseRecordId
      ? `${orgId}/${progId}/${caseRecordId}/${assetId}`
      : null;

  const cleanup = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (audioRef.current) {
      audioRef.current.pause();
      const src = audioRef.current.src;
      audioRef.current.removeAttribute('src');
      audioRef.current.load(); // reset the element
      if (src.startsWith('blob:')) URL.revokeObjectURL(src);
    }

    mediaSourceRef.current = null;
  }, []);

  const startStreaming = useCallback(async () => {
    if (!assetId || !orgId || !progId || !caseRecordId) return;

    cleanup();
    setPhase('connecting');
    setProgress({ received: 0, total: null });
    setError(null);
    setDuration(0);
    setBufferedEnd(0);

    const audio = audioRef.current;
    if (!audio) return;

    const url = buildDownloadUrl(orgId, progId, caseRecordId, assetId);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetchWithAuth(url, controller.signal);
      await pipeResponseToAudio(
        audio,
        response,
        controller.signal,
        setPhase,
        setProgress,
        setBufferedEnd,
      );
      setPhase('ready');
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('Streaming audio error:', err);
      setError(err.message || 'Failed to load audio');
      setPhase('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetKey, cleanup]);

  // Create audio element once and attach duration listeners
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
    }
    const audio = audioRef.current;

    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onDurationChange = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onProgress = () => {
      if (audio.buffered.length > 0) {
        setBufferedEnd(audio.buffered.end(audio.buffered.length - 1));
      }
    };

    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('progress', onProgress);

    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('progress', onProgress);
      cleanup();
    };
  }, [cleanup]);

  // Start streaming when asset changes
  useEffect(() => {
    if (assetKey) {
      startStreaming();
    } else {
      cleanup();
      setPhase('idle');
    }
  }, [assetKey, retryCount, startStreaming, cleanup]);

  const retry = useCallback(() => setRetryCount(c => c + 1), []);

  return { audioRef, phase, progress, duration, bufferedEnd, error, retry };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Fetch with auth headers, retrying once on 401 after token refresh. */
async function fetchWithAuth(url: string, signal: AbortSignal): Promise<Response> {
  let headers = buildStreamHeaders();
  let response = await fetch(url, { headers, credentials: 'include', signal });

  if (response.status === 401) {
    await refreshAccessToken();
    headers = buildStreamHeaders();
    response = await fetch(url, { headers, credentials: 'include', signal });
  }

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status} ${response.statusText}`);
  }
  return response;
}

/** Parse the response and pipe audio data to the audio element via MSE or blob fallback. */
async function pipeResponseToAudio(
  audio: HTMLAudioElement,
  response: Response,
  signal: AbortSignal,
  setPhase: (p: Phase) => void,
  setProgress: React.Dispatch<React.SetStateAction<StreamingProgress>>,
  setBufferedEnd: React.Dispatch<React.SetStateAction<number>>,
): Promise<void> {
  const contentType = response.headers.get('Content-Type');
  const contentLength = response.headers.get('Content-Length');
  const totalBytes = contentLength ? parseInt(contentLength, 10) : null;
  setProgress(p => ({ ...p, total: totalBytes }));

  const reader = response.body?.getReader();
  if (!reader) throw new Error('ReadableStream not supported');

  const mseMime = getMSEMimeType(contentType);
  const mseSupported = typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(mseMime);

  if (mseSupported) {
    await streamViaMSE(
      audio,
      reader,
      mseMime,
      totalBytes,
      signal,
      setPhase,
      setProgress,
      setBufferedEnd,
    );
  } else {
    await streamViaBlob(audio, reader, contentType, totalBytes, signal, setPhase, setProgress);
    // For blob fallback, bufferedEnd is tracked via the audio progress event listener
  }
}

// ---------------------------------------------------------------------------
// Buffer management
// ---------------------------------------------------------------------------

/** Pause fetching when this many seconds of audio are buffered ahead. */
const FORWARD_BUFFER_LIMIT = 10;
/** Resume fetching when forward buffer drops to this many seconds. */
const FORWARD_BUFFER_RESUME = 5;

/** Seconds of audio buffered ahead of the current playback position. */
function getForwardBuffer(audio: HTMLAudioElement, sourceBuffer: SourceBuffer): number {
  const { buffered } = sourceBuffer;
  if (buffered.length === 0) return 0;
  for (let i = 0; i < buffered.length; i++) {
    if (audio.currentTime <= buffered.end(i) + 0.1) {
      return buffered.end(i) - audio.currentTime;
    }
  }
  return 0;
}

/**
 * Pause reading from the fetch stream until the forward buffer drops below
 * the resume threshold. Resolves immediately if buffer is within limits.
 */
async function waitForBufferSpace(
  audio: HTMLAudioElement,
  sourceBuffer: SourceBuffer,
  signal: AbortSignal,
): Promise<void> {
  if (getForwardBuffer(audio, sourceBuffer) <= FORWARD_BUFFER_LIMIT) return;

  return new Promise<void>((resolve, reject) => {
    const check = () => {
      if (signal.aborted) {
        detach();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      if (getForwardBuffer(audio, sourceBuffer) <= FORWARD_BUFFER_RESUME) {
        detach();
        resolve();
      }
    };

    const onAbort = () => {
      detach();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const detach = () => {
      audio.removeEventListener('timeupdate', check);
      audio.removeEventListener('seeking', check);
      audio.removeEventListener('play', check);
      signal.removeEventListener('abort', onAbort);
    };

    audio.addEventListener('timeupdate', check);
    audio.addEventListener('seeking', check);
    audio.addEventListener('play', check);
    signal.addEventListener('abort', onAbort);
  });
}

/**
 * Update the buffered-end state and estimate total duration on the MediaSource.
 * Estimating duration from (buffered seconds / bytes received) * total bytes
 * lets audio.duration report a finite value during streaming (before endOfStream),
 * which the seekbar, time display, and transcript-click seeking all depend on.
 */
function updateBufferedState(
  mediaSource: MediaSource,
  sourceBuffer: SourceBuffer,
  received: number,
  totalBytes: number | null,
  setBufferedEnd: React.Dispatch<React.SetStateAction<number>>,
): void {
  if (sourceBuffer.buffered.length === 0) return;
  const buffEnd = sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1);
  setBufferedEnd(buffEnd);

  if (!totalBytes || buffEnd <= 0 || received <= 0) return;
  const estimated = (buffEnd / received) * totalBytes;
  try {
    if (!Number.isFinite(mediaSource.duration) || estimated > mediaSource.duration) {
      mediaSource.duration = estimated;
    }
  } catch {
    // Ignore — SourceBuffer may be updating or MediaSource closed
  }
}

// ---------------------------------------------------------------------------
// Streaming strategies
// ---------------------------------------------------------------------------

/** Stream audio via MediaSource Extensions — true streaming, no full download. */
async function streamViaMSE(
  audio: HTMLAudioElement,
  reader: ReadableStreamDefaultReader<Uint8Array>,
  mime: string,
  totalBytes: number | null,
  signal: AbortSignal,
  setPhase: (p: Phase) => void,
  setProgress: React.Dispatch<React.SetStateAction<StreamingProgress>>,
  setBufferedEnd: React.Dispatch<React.SetStateAction<number>>,
): Promise<void> {
  const mediaSource = new MediaSource();
  const mseUrl = URL.createObjectURL(mediaSource);
  audio.src = mseUrl;

  // Wait for sourceopen
  await new Promise<void>((resolve, reject) => {
    mediaSource.addEventListener('sourceopen', () => resolve(), { once: true });
    mediaSource.addEventListener('error', () => reject(new Error('MediaSource error')), {
      once: true,
    });
    // If the signal aborts before sourceopen, reject
    signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {
      once: true,
    });
  });

  const sourceBuffer = mediaSource.addSourceBuffer(mime);
  let received = 0;

  setPhase('streaming');

  while (true) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

    // Flow control: pause reading if forward buffer exceeds limit
    await waitForBufferSpace(audio, sourceBuffer, signal);

    const { done, value } = await reader.read();
    if (done) break;

    // Wait for sourceBuffer to be ready
    if (sourceBuffer.updating) {
      await new Promise<void>(resolve =>
        sourceBuffer.addEventListener('updateend', () => resolve(), { once: true }),
      );
    }

    sourceBuffer.appendBuffer(new Uint8Array(value).buffer as ArrayBuffer);

    // Wait for append to complete
    await new Promise<void>(resolve =>
      sourceBuffer.addEventListener('updateend', () => resolve(), { once: true }),
    );

    received += value.byteLength;
    setProgress({ received, total: totalBytes });

    // Update buffered range and estimate total duration for seekbar/time display
    updateBufferedState(mediaSource, sourceBuffer, received, totalBytes, setBufferedEnd);
  }

  // Signal end of stream — wait for the demuxer to initialize first.
  // Calling endOfStream before HAVE_METADATA causes a pipeline error.
  if (sourceBuffer.updating) {
    await new Promise<void>(resolve =>
      sourceBuffer.addEventListener('updateend', () => resolve(), { once: true }),
    );
  }
  if (mediaSource.readyState === 'open') {
    if (audio.readyState < 1) {
      // Wait for the browser to parse enough frames for metadata
      await new Promise<void>((resolve, reject) => {
        const onReady = () => {
          detach();
          resolve();
        };
        const onError = () => {
          detach();
          reject(new Error('Audio metadata error'));
        };
        const detach = () => {
          audio.removeEventListener('loadedmetadata', onReady);
          audio.removeEventListener('error', onError);
        };
        audio.addEventListener('loadedmetadata', onReady, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });
    }
    try {
      mediaSource.endOfStream();
    } catch {
      // Ignore — MediaSource may have been closed between the check and call
    }
  }
}

/** Fallback: accumulate chunks into a Blob, then set as audio src. */
async function streamViaBlob(
  audio: HTMLAudioElement,
  reader: ReadableStreamDefaultReader<Uint8Array>,
  contentType: string | null,
  totalBytes: number | null,
  signal: AbortSignal,
  setPhase: (p: Phase) => void,
  setProgress: React.Dispatch<React.SetStateAction<StreamingProgress>>,
): Promise<void> {
  const chunks: Uint8Array[] = [];
  let received = 0;
  setPhase('streaming');

  while (true) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    setProgress({ received, total: totalBytes });
  }

  const blob = new Blob(chunks as BlobPart[], { type: contentType || 'audio/mpeg' });
  audio.src = URL.createObjectURL(blob);
}
